/**
 * vigilEnding — what the night came to.
 *
 * The spec asks for "an ending that lands — dawn should feel earned, not like a
 * timer expiring", and until now there was no ending at all: the vigil reached
 * `over` and the world clock rolled straight on into another day and another
 * dusk, forever. `NightVigil.onComplete` had never been wired to anything.
 *
 * This turns the run into a sentence. Two rules shape it, both from the spec:
 *
 * - **Show what was found, never what remains.** No "3 of 5 sources gathered",
 *   no score, no percentage. The line reports what happened and says nothing
 *   about what a different run might have contained.
 * - **Loss is impossible; only the shape of dawn varies.** So every ending is a
 *   morning, and none of them is a failure. A fire that went out is not a loss
 *   — it is a fire that was spent, which is what fires are for. The lines are
 *   written so that no reading of them is a scolding.
 *
 * One line, per "the scene teaches by composition, light, and one line of text
 * at most". The optional second clause is part of the same sentence, and only
 * appears if it was earned.
 */

/** What the run actually consisted of, as far as the ending cares. */
export interface VigilOutcome {
  /**
   * Fire fuel at the moment the DARK ended, 0..1 — first light, not the end of
   * the run.
   *
   * The distinction is the whole of a real bug. `burn()` is gated on
   * `vigil.getDarkRemaining() > 0`, so from dawn onward nothing consumes fuel
   * while the fire can still be fed. Reading this at the end of the vigil made
   * the last 180 s a window where fuel only ever went up: a fire allowed to die
   * at t=382 and rebuilt from three armfuls during the morning reported "The
   * fire outlasted the night". It did not. Falsity in the one sentence the game
   * says at the end is worse than any number in it being off.
   */
  fuelAtDawn: number;
  /** Did something come to the rim of the light? */
  visitorCameNear: boolean;
  /** Was it at least seen at the treeline? */
  visitorWatched: boolean;
  /** Did the lantern ever leave its plinth? */
  lanternCarried: boolean;
}

/**
 * A run that has not happened yet.
 *
 * Exported so the scene declares and *resets* from one place. The bug this
 * closes: the outcome was a class field written in three places and cleared in
 * none, so re-entering the grove for a second vigil inherited the first one's
 * facts and the closing line claimed company on a night nothing came to. Two
 * hand-written copies of this shape would drift the same way again.
 */
export function initialOutcome(): VigilOutcome {
  return {
    fuelAtDawn: 0,
    visitorCameNear: false,
    visitorWatched: false,
    lanternCarried: false,
  };
}

/**
 * Records what the night consisted of, and stops listening when it ends.
 *
 * Every field here has the same exposure, and it is worth stating once: from
 * first light onward nothing burns fuel and the sky is lifting, so anything
 * still writing to the outcome is describing the morning while the closing
 * line claims to describe the night. That produced a real falsity — a fire
 * allowed to die at t=382 and rebuilt from three armfuls at sunrise reported
 * "The fire outlasted the night".
 *
 * Fixing only the fuel would have left the visitor flags resting on a
 * coincidence: the darkness curve happens to fall below the visitor's
 * threshold about 0.8 s before dawn, so it cannot presently be drawn closer by
 * a morning fire. That is four independently tunable constants agreeing by
 * luck, not a guarantee. Sealing is the guarantee.
 *
 * Phaser-free on purpose. The scene wiring this replaced had no test coverage
 * at all — the fix could have been reverted wholesale with the suite still
 * green.
 */
export class OutcomeRecorder {
  private outcome: VigilOutcome = initialOutcome();
  private sealed = false;

  /** Note where the visitor has got to. Ignored once the night is over. */
  noteVisitor(stage: 'absent' | 'watching' | 'near'): void {
    if (this.sealed) return;
    if (stage === 'watching' || stage === 'near') this.outcome.visitorWatched = true;
    if (stage === 'near') this.outcome.visitorCameNear = true;
  }

  /** Note that the lantern left its plinth. Ignored once the night is over. */
  noteLanternCarried(): void {
    if (this.sealed) return;
    this.outcome.lanternCarried = true;
  }

  /**
   * First light. Take the fire's final reading and stop listening.
   *
   * Idempotent: called again — by the completion fallback, or by a second dawn
   * if the clock is ever driven oddly — it changes nothing.
   */
  seal(fuelAtDawn: number): void {
    if (this.sealed) return;
    this.outcome.fuelAtDawn = fuelAtDawn;
    this.sealed = true;
  }

  isSealed(): boolean {
    return this.sealed;
  }

  /** A copy, so nothing downstream can write to the record after the fact. */
  get(): VigilOutcome {
    return { ...this.outcome };
  }

  reset(): void {
    this.outcome = initialOutcome();
    this.sealed = false;
  }
}

/** Above this, the fire is unmistakably still going at first light. */
export const ENDING_ALIVE_FUEL = 0.35;

/** Above this it is embers, but embers are still a fire. */
export const ENDING_EMBER_FUEL = 0.08;

/**
 * The closing line.
 *
 * The fire clause is always present, because the fire is the game. The second
 * clause is the one that varies most between runs, and it exists so that a
 * player who found the thing nobody told them about gets that reflected back
 * without ever being told it was a thing to find.
 */
export function endingLine(outcome: VigilOutcome): string {
  const fire = fireClause(outcome.fuelAtDawn);
  const company = companyClause(outcome);
  return company ? `${fire}, ${company}` : `${fire}.`;
}

function fireClause(fuel: number): string {
  if (fuel >= ENDING_ALIVE_FUEL) return 'The fire outlasted the night';
  if (fuel >= ENDING_EMBER_FUEL) return 'It was still burning when the light came';
  // Deliberately not "you let it go out". The fire was spent, which is what a
  // night's fire is for, and dawn arrived regardless — as it always does here.
  return 'The last of it went out as the sky turned';
}

function companyClause(outcome: VigilOutcome): string | null {
  if (outcome.visitorCameNear) return 'and you were not alone in it';
  if (outcome.visitorWatched) return 'and something had been watching';
  if (outcome.lanternCarried) return 'and the lantern is still out there somewhere';
  return null;
}
